/**
 * Admin list: official Practice levels — rename, description, category, author, delete,
 * and "Load in canvas" to open the practice editor.
 */
import {
    deleteAdminPracticeLevel,
    fetchPracticeHubConfig,
    setPracticeShowInCategories,
    updateAdminPracticeLevelMeta,
} from "../lib/cloud";
import {
    PRACTICE_CATEGORIES,
    normalizePracticeLevelData,
    isPracticeCategory,
    type PracticeLevelDef,
} from "./practice-levels";

export type AdminPracticeLevelsUiOpts = {
    onLevelsChanged: () => void;
    onEditLayout: (levelIndex: number, def: PracticeLevelDef) => void;
    onShowInCategoriesChanged?: (show: boolean) => void;
};

export function mountAdminPracticeLevelsList(mount: HTMLElement, opts: AdminPracticeLevelsUiOpts) {
    const root = document.createElement("div");
    root.className = "adminSpeedrunLevelsList";

    let showInCategories = false;

    function renderHubToggle(parent: HTMLElement) {
        const box = document.createElement("div");
        box.className = "modeBox adminPracticeHubToggle";

        const row = document.createElement("div");
        row.className = "adminPracticeHubToggleRow";

        const label = document.createElement("span");
        label.className = "formLabel";
        label.textContent = "Show In Categories";

        const hint = document.createElement("p");
        hint.className = "accessHint";
        hint.textContent = "When on, Practice appears in the Game Hub category grid.";

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "toggleSwitch";
        btn.setAttribute("aria-label", "Show In Categories");
        btn.setAttribute("aria-pressed", showInCategories ? "true" : "false");
        btn.classList.toggle("toggleOn", showInCategories);
        btn.onclick = () => {
            void (async () => {
                const next = !showInCategories;
                btn.disabled = true;
                try {
                    showInCategories = await setPracticeShowInCategories(next);
                    btn.setAttribute("aria-pressed", showInCategories ? "true" : "false");
                    btn.classList.toggle("toggleOn", showInCategories);
                    opts.onShowInCategoriesChanged?.(showInCategories);
                } catch {
                    alert("Could not update hub visibility (admin session / network).");
                } finally {
                    btn.disabled = false;
                }
            })();
        };

        row.append(label, btn);
        box.append(row, hint);
        parent.appendChild(box);
    }

    async function refresh() {
        root.replaceChildren();
        renderHubToggle(root);

        let levels: Awaited<ReturnType<typeof fetchPracticeHubConfig>>["levels"];
        try {
            const hub = await fetchPracticeHubConfig();
            levels = hub.levels;
            showInCategories = hub.showInCategories;
            const toggle = root.querySelector<HTMLButtonElement>(".adminPracticeHubToggle .toggleSwitch");
            if (toggle) {
                toggle.setAttribute("aria-pressed", showInCategories ? "true" : "false");
                toggle.classList.toggle("toggleOn", showInCategories);
            }
        } catch {
            const p = document.createElement("p");
            p.className = "accessHint";
            p.textContent = "Could not load practice levels (check admin session / database).";
            root.appendChild(p);
            return;
        }

        if (!levels.length) {
            const p = document.createElement("p");
            p.className = "accessHint";
            p.textContent = "No official practice levels in the database yet.";
            root.appendChild(p);
            return;
        }

        for (const row of levels) {
            const rowEl = document.createElement("div");
            rowEl.className = "adminLevelRow modeBox";

            const titleEl = document.createElement("div");
            titleEl.className = "adminLevelRowTitle";
            titleEl.textContent = `Practice Level ${row.levelIndex}`;

            const nameLab = document.createElement("label");
            nameLab.className = "formLabel";
            nameLab.textContent = "Display name";
            const nameInp = document.createElement("input");
            nameInp.type = "text";
            nameInp.className = "formInput";
            nameInp.maxLength = 120;
            nameInp.placeholder = `Practice Level ${row.levelIndex}`;
            nameInp.value = row.displayName ?? "";

            const descLab = document.createElement("label");
            descLab.className = "formLabel";
            descLab.textContent = "Description (shown on level card)";
            const descInp = document.createElement("textarea");
            descInp.className = "formInput adminLevelDescInput";
            descInp.maxLength = 500;
            descInp.rows = 3;
            descInp.placeholder = "Short description for the level picker card";
            descInp.value = row.description ?? "";

            const catLab = document.createElement("label");
            catLab.className = "formLabel";
            catLab.textContent = "Category";
            const catSel = document.createElement("select");
            catSel.className = "formInput";
            const blankOpt = document.createElement("option");
            blankOpt.value = "";
            blankOpt.textContent = "— none —";
            catSel.appendChild(blankOpt);
            for (const c of PRACTICE_CATEGORIES) {
                const o = document.createElement("option");
                o.value = c;
                o.textContent = c;
                o.selected = row.category === c;
                catSel.appendChild(o);
            }

            const authorLab = document.createElement("label");
            authorLab.className = "formLabel";
            authorLab.textContent = "Author (Made by…)";
            const authorInp = document.createElement("input");
            authorInp.type = "text";
            authorInp.className = "formInput";
            authorInp.maxLength = 120;
            authorInp.placeholder = "Creator display name";
            authorInp.value = row.authorName ?? "";

            const btnRow = document.createElement("div");
            btnRow.className = "adminLevelRowBtns";

            const saveMeta = document.createElement("button");
            saveMeta.type = "button";
            saveMeta.className = "btn";
            saveMeta.textContent = "Save card info";

            const del = document.createElement("button");
            del.type = "button";
            del.className = "btn";
            del.textContent = "Delete level";

            const edit = document.createElement("button");
            edit.type = "button";
            edit.className = "btn green";
            edit.textContent = "Load in canvas";

            saveMeta.onclick = () => {
                void (async () => {
                    try {
                        const displayName = nameInp.value.trim();
                        const description = descInp.value.trim();
                        const authorName = authorInp.value.trim();
                        const category = catSel.value;
                        await updateAdminPracticeLevelMeta(row.levelIndex, {
                            displayName: displayName.length ? displayName : null,
                            description: description.length ? description : null,
                            category: isPracticeCategory(category) ? category : null,
                            authorName: authorName.length ? authorName : null,
                        });
                        await refresh();
                        opts.onLevelsChanged();
                    } catch {
                        alert("Could not save card info (admin session / network).");
                    }
                })();
            };

            del.onclick = () => {
                if (!confirm(`Delete official practice level ${row.levelIndex} permanently?`)) return;
                void (async () => {
                    try {
                        await deleteAdminPracticeLevel(row.levelIndex);
                        await refresh();
                        opts.onLevelsChanged();
                    } catch {
                        alert("Could not delete (admin session / network).");
                    }
                })();
            };

            edit.onclick = () => {
                if (!row.data) {
                    alert("This level has no layout data.");
                    return;
                }
                opts.onEditLayout(row.levelIndex, normalizePracticeLevelData(row.data));
            };

            btnRow.append(saveMeta, del, edit);

            rowEl.append(
                titleEl,
                nameLab, nameInp,
                descLab, descInp,
                catLab, catSel,
                authorLab, authorInp,
                btnRow,
            );
            root.appendChild(rowEl);
        }
    }

    mount.replaceChildren();
    mount.appendChild(root);
    void refresh();

    return {
        destroy() {
            mount.replaceChildren();
        },
        refresh: () => void refresh(),
    };
}
